import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { useCustomer } from '../../context/CustomerContext';
import { usePromo2x1, etiquetaOferta, precioConDescuento, buyGetDe, descuentoDe } from '../../hooks/usePromo2x1';
import { isProductInStock } from '../../utils/stock';

const badgeStyles = {
  launch: {
    text: 'Lanzamiento',
    bgColor: 'bg-[#1a1a1a]'
  },
  essential: {
    text: 'Esencial en tu rutina',
    bgColor: 'bg-[#1a1a1a]'
  },
  basic: {
    text: 'Básico en tu rutina',
    bgColor: 'bg-[#1a1a1a]'
  }
};

// Loading dots para botón
function ButtonLoadingDots() {
  return (
    <span className="flex items-center justify-center gap-1.5 h-4">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-white"
          style={{
            animation: 'buttonDotPulse 0.6s ease-in-out infinite',
            animationDelay: `${i * 150}ms`
          }}
        />
      ))}
      <style>{`
        @keyframes buttonDotPulse {
          0%, 100% { opacity: 0.4; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </span>
  );
}

export default function ProductCard({ product, showDetails = false, minimal = false }) {
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { isDistributor } = useCustomer();
  const promo2x1 = usePromo2x1();
  // Si el producto viene de `useProducts` (shop) ya trae inStock booleano.
  // Si viene crudo del backend (carruseles de recomendaciones/sinergia),
  // calculamos el stock a partir de los campos de inventario de las variantes.
  const inStock = typeof product.inStock === 'boolean'
    ? product.inStock
    : isProductInStock(product);
  // Un producto puede llevar un "compra y llévate" (2x1/3x2) y/o un % a la vez.
  const ofertasProd = promo2x1.ofertas[product.id] || [];
  const ofertaBuyGet = buyGetDe(ofertasProd);
  const ofertaDesc = descuentoDe(ofertasProd);
  const hasOferta = promo2x1.productIds.has(product.id);
  // Etiquetas de los badges (buy-get + %); fallback "2x1" si venía solo por variantes.
  const badgeLabels = [];
  if (ofertaBuyGet) badgeLabels.push(etiquetaOferta(ofertaBuyGet));
  if (ofertaDesc) badgeLabels.push(etiquetaOferta(ofertaDesc));
  if (!badgeLabels.length && hasOferta) badgeLabels.push('2x1');

  // Obtener la primera variante - usa 'id' para Medusa, 'value' como fallback
  const getVariantId = (variant) => variant?.id || variant?.value || '';

  const [selectedVariantId, setSelectedVariantId] = useState(
    getVariantId(product.variants?.[0])
  );
  const isFavorite = isInWishlist(product.id);
  const [imageError, setImageError] = useState(false);
  const [buttonState, setButtonState] = useState('idle');
  const buttonTimerRef = useRef(null);

  useEffect(() => {
    return () => { if (buttonTimerRef.current) clearTimeout(buttonTimerRef.current); };
  }, []);

  const isVariable = product.variants && product.variants.length > 1;

  // Encontrar variante actual por ID o value
  const currentVariant = product.variants?.find(v => 
    v.id === selectedVariantId || v.value === selectedVariantId
  );
  
  const currentImage = currentVariant?.image || currentVariant?.thumbnail || product.image || product.thumbnail;
  
  // Precio: Medusa usa calculated_price.calculated_amount (en pesos) o price directo
  const getPrice = (variant) => {
    if (variant?.calculated_price?.calculated_amount) {
      return variant.calculated_price.calculated_amount;
    }
    return variant?.price || product.price || 0;
  };
  
  const basePrice = getPrice(currentVariant);
  // Con % de descuento activo se muestra el precio rebajado y el original tachado
  // (el 3x2/2x1 no cambia el precio unitario: se aplica en el carrito).
  const rebajado = precioConDescuento(basePrice, ofertaDesc, currentVariant?.id);
  const currentPrice = rebajado ?? basePrice;
  const currentOriginalPrice = rebajado != null ? basePrice : currentVariant?.originalPrice || product.originalPrice;

  const minPrice = isVariable
    ? Math.min(...(product.variants || []).map(v => precioConDescuento(getPrice(v), ofertaDesc, v.id) ?? getPrice(v)))
    : getPrice(currentVariant);

  // Precio del programa de distribuidoras (si el variant lo tiene anexado por client.js).
  // Sólo se muestra a clientas con sesión iniciada como distribuidora APROBADA.
  const currentDistributorPrice =
    typeof currentVariant?.distributor_amount === 'number'
      ? currentVariant.distributor_amount
      : null;

  const renderPriceBlock = (justify = 'justify-center') => {
    const showDual =
      isDistributor &&
      currentDistributorPrice != null &&
      currentDistributorPrice < currentPrice;
    if (showDual) {
      return (
        <div className={`flex items-baseline gap-3 mb-3 ${justify}`}>
          <div className="flex flex-col items-start leading-tight">
            <span className="text-[10px] tracking-[0.16em] uppercase text-[var(--phys-fog)]">Público</span>
            <span className="text-sm font-light text-[var(--phys-slate)]">${currentPrice}</span>
          </div>
          <div className="flex flex-col items-start leading-tight">
            <span className="text-[10px] tracking-[0.16em] uppercase text-[var(--phys-steel)]">Socio</span>
            <span className="text-sm font-normal underline text-[var(--phys-ink)]">${currentDistributorPrice}</span>
          </div>
        </div>
      );
    }
    return (
      <div className={`flex items-center gap-2 mb-3 ${justify}`}>
        {currentOriginalPrice && (
          <span className="text-xs text-[var(--phys-fog)] line-through font-light">
            ${currentOriginalPrice}
          </span>
        )}
        <span className="text-sm font-normal underline">
          ${currentPrice || product.price}
        </span>
      </div>
    );
  };

  const getProductSize = () => {
    if (product.variants?.length === 1) {
      return product.variants[0].title || product.variants[0].label || product.variants[0].percentage;
    }
    return null;
  };

  const getVariantSummary = () => {
    if (!isVariable) return null;
    const count = product.variants.length;
    const hasPercentage = product.variants.some(v => v.percentage);
    const hasSize = product.variants.some(v => v.label?.includes('mL') || v.title?.includes('mL'));
    
    if (hasPercentage) return `${count} Porcentajes`;
    if (hasSize) return `${count} Tamaños`;
    return `${count} Opciones`;
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (buttonState !== 'idle') return;
    
    setButtonState('loading');
    
    try {
      // Pasar el producto y el ID de la variante seleccionada (ID de Medusa)
      await addToCart(product, selectedVariantId, 1);
      setButtonState('added');
    } catch (error) {
      console.error('Error adding to cart:', error);
      setButtonState('idle');
      return;
    }
    
    buttonTimerRef.current = setTimeout(() => setButtonState('idle'), 2000);
  };

  const toggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist({
      id: product.id,
      title: product.title || product.name,
      name: product.title || product.name,
      thumbnail: product.thumbnail || product.image,
      image: product.thumbnail || product.image,
      price: currentPrice,
      handle: product.handle || product.id,
    });
  };

  const handleVariantChange = (e, variant) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedVariantId(getVariantId(variant));
    setImageError(false);
  };

  const getButtonContent = () => {
    switch (buttonState) {
      case 'loading':
        return <ButtonLoadingDots />;
      case 'added':
        return (
          <span className="flex items-center justify-center gap-2 h-4">
            <span>Añadido</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
        );
      default:
        return <span className="h-4 flex items-center justify-center">Añadir a la cesta</span>;
    }
  };

  // Mobile: botones más compactos, sentence case
  const getMobileButtonClasses = () => {
    const base = 'w-full py-2.5 px-4 text-xs tracking-wide font-medium transition-all duration-300';
    if (!inStock) return `${base} bg-[var(--phys-frost)] text-[var(--phys-fog)] cursor-not-allowed`;
    if (buttonState === 'loading') return `${base} bg-[#1a1a1a] text-white cursor-wait`;
    if (buttonState === 'added') return `${base} bg-[var(--phys-ice)] text-white`;
    return `${base} bg-[#1a1a1a] text-white hover:bg-[var(--phys-ink)]`;
  };

  // Desktop: uppercase
  const getDesktopButtonClasses = () => {
    const base = 'w-full py-5 px-4 text-sm tracking-wide font-medium transition-all duration-300';
    if (!inStock) return `${base} bg-[var(--phys-frost)] text-[var(--phys-fog)] cursor-not-allowed`;
    if (buttonState === 'loading') return `${base} bg-[#1a1a1a] text-white cursor-wait`;
    if (buttonState === 'added') return `${base} bg-[var(--phys-ice)] text-white`;
    return `${base} bg-[#1a1a1a] text-white hover:bg-[var(--phys-ink)]`;
  };

  // Obtener label de variante (Medusa usa 'title', local usa 'label' o 'percentage')
  const getVariantLabel = (variant) => {
    return variant?.title || variant?.percentage || variant?.label || '';
  };

  const productSize = getProductSize();
  const productName = product.title || product.name;

  // URL del producto - usa handle si existe, sino id
  const productUrl = `/products/${product.handle || product.id}`;

  return (
    <div className="group relative flex flex-col h-full bg-[var(--phys-snow)] md:hover:bg-[var(--phys-mist)] transition-colors duration-300">
      {/* ============================================
          IMAGEN - Fondo #F6F5E7 siempre visible
          ============================================ */}
      <Link to={productUrl} className="relative aspect-square overflow-hidden bg-[var(--phys-mist)] block">
        {/* Badges (Agotado primero; luego ofertas 2x1/3x2 y/o -% y badge del producto) */}
        {(!inStock || badgeLabels.length > 0 || (product.badge && badgeStyles[product.badge])) && (
          <div className="absolute top-3 left-3 z-10 flex flex-col items-start gap-1">
            {!inStock && (
              <span className="inline-block px-2 py-1 text-[10px] tracking-wider font-medium text-white bg-[var(--phys-slate)] uppercase">
                Agotado
              </span>
            )}
            {badgeLabels.map((label) => (
              <span key={label} className="inline-block px-2 py-1 text-[10px] tracking-wider font-medium text-white bg-[var(--phys-steel)]">
                {label}
              </span>
            ))}
            {product.badge && badgeStyles[product.badge] && (
              <span className={`
                inline-block px-2 py-1
                text-[10px] tracking-wider font-light
                text-white ${badgeStyles[product.badge].bgColor}
              `}>
                {badgeStyles[product.badge].text}
              </span>
            )}
          </div>
        )}

        {/* Favorito */}
        <button
          onClick={toggleFavorite}
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center hover:scale-110 transition-all"
          aria-label="Add to favorites"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill={isFavorite ? '#C17817' : 'none'}
            stroke={isFavorite ? '#C17817' : '#333'}
            strokeWidth="1.5"
          >
            <path d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.82 1.5 6.5C1.5 3.86 3.525 1.5 6 1.5C7.44 1.5 8.82 2.19 9.75 3.27H10.25C11.18 2.19 12.56 1.5 14 1.5C16.475 1.5 18.5 3.86 18.5 6.5C18.5 9.82 15.6 12.46 11.175 16.45L10 17.5Z"/>
          </svg>
        </button>

        {/* Imagen */}
        {!imageError ? (
          <img
            src={currentImage}
            alt={productName}
            className="absolute inset-0 w-full h-full object-contain"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="absolute inset-0 w-full h-full flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-[var(--phys-fog)]">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <path d="M8 38L18 24L26 34L32 26L40 38" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="32" cy="16" r="4" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
              <span className="text-[10px] uppercase tracking-wider font-light">Imagen no disponible</span>
            </div>
          </div>
        )}
      </Link>

      {/* ============================================
          INFO MINIMAL — sólo nombre + subtítulo, alineado a la derecha
          (usado en el carrusel "Otras recomendaciones")
          ============================================ */}
      {minimal ? (
        <div className="flex-1 flex flex-col px-[10px] md:px-[20px] pt-4 pb-[15px] md:pb-[20px] text-right">
          <Link to={productUrl}>
            <h3 className="text-sm font-normal tracking-wide leading-snug hover:opacity-60 transition-opacity">
              {productName}
            </h3>
          </Link>
          {(product.subtitle || product.description) && (
            <p className="text-sm text-[var(--phys-slate)] leading-relaxed font-light mt-1">
              {product.subtitle || product.description}
            </p>
          )}
        </div>
      ) : (
      /* ============================================
          INFO DEL PRODUCTO - Con padding interno
          ============================================ */
      <div className="flex-1 flex flex-col px-[10px] md:px-0 pt-4 pb-[15px] md:pb-0">
        {/* Nombre - Clickeable */}
        <Link to={productUrl}>
          <h3 className="text-sm font-normal tracking-wide mb-2 leading-snug text-left md:text-center hover:opacity-60 transition-opacity">
            {productName}
          </h3>
        </Link>

        {/* Descripción */}
        <p className="text-sm text-[var(--phys-slate)] leading-relaxed mb-2 font-light text-left md:text-center">
          {product.description}
        </p>

        {/* Detalles adicionales del producto */}
        {showDetails && product.details && (
          <div className="text-xs text-[var(--phys-slate)] leading-relaxed mb-4 font-light text-left md:text-center space-y-2">
            {product.details.aroma && (
              <p><strong>Aroma:</strong> {product.details.aroma}</p>
            )}
            {product.details.dimensions && (
              <p><strong>Dimensions:</strong> {product.details.dimensions}</p>
            )}
            {product.details.ingredients && (
              <div className="mt-2">
                <p className="font-medium">Ingredientes activos</p>
                <p>{product.details.ingredients}</p>
              </div>
            )}
          </div>
        )}

        {/* ============================================
            PRODUCTO VARIABLE
            ============================================ */}
        {isVariable && (
          <>
           {/* MOBILE: Siempre visible */}
<div className="md:hidden mt-auto">
  <div className="mb-3">
    <div className="relative">
      <select
        value={selectedVariantId}
        onChange={(e) => {
          const variant = product.variants.find(v => getVariantId(v) === e.target.value);
          if (variant) handleVariantChange(e, variant);
        }}
        onClick={(e) => e.stopPropagation()}
        className="w-full appearance-none bg-transparent border-0 px-0 py-2 pr-6 text-xs tracking-wide focus:outline-none cursor-pointer"
      >
        {product.variants.map((variant) => (
          <option key={getVariantId(variant)} value={getVariantId(variant)}>
            {getVariantLabel(variant)}
          </option>
        ))}
      </select>
      <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none">
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
          <path d="M1 1L6 6L11 1" stroke="#333" strokeWidth="1.5"/>
        </svg>
      </div>
    </div>
  </div>

  {renderPriceBlock('justify-start')}

  <button
    onClick={handleAddToCart}
    disabled={!inStock || buttonState === 'loading'}
    className={getMobileButtonClasses()}
  >
    {inStock ? getButtonContent() : 'Agotado'}
  </button>
</div>

            {/* DESKTOP: estructura hover reserva el espacio; resumen se sobrepone en la fila de swatches */}
            <div className="hidden md:flex flex-col mt-auto">
              <div className="relative">
                {/* Hover: Swatches + Precio + Botón (siempre renderizado para reservar altura) */}
                <div className="opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200">
                  <div className="flex justify-center gap-3 mb-3">
                    {product.variants.map((variant) => {
                      const variantId = getVariantId(variant);
                      return (
                        <label
                          key={variantId}
                          className="flex items-center gap-1.5 cursor-pointer group/variant"
                          onClick={(e) => handleVariantChange(e, variant)}
                        >
                          <div className="relative">
                            <input
                              type="radio"
                              name={`variant-${product.id}`}
                              value={variantId}
                              checked={selectedVariantId === variantId}
                              onChange={() => {}}
                              className="sr-only"
                            />
                            <div className={`
                              w-4 h-4 rounded-full border transition-all duration-200
                              flex items-center justify-center
                              ${selectedVariantId === variantId
                                ? 'border-[var(--phys-ink)]'
                                : 'border-[var(--phys-frost)] group-hover/variant:border-[var(--phys-slate)]'
                              }
                            `}>
                              <div className={`
                                w-2 h-2 rounded-full transition-all duration-200
                                ${selectedVariantId === variantId
                                  ? 'bg-[#1a1a1a] scale-100'
                                  : 'bg-transparent scale-0'
                                }
                              `} />
                            </div>
                          </div>
                          <span className={`
                            text-sm tracking-wide transition-colors
                            ${selectedVariantId === variantId
                              ? 'text-[var(--phys-ink)]'
                              : 'text-[var(--phys-slate)] group-hover/variant:text-[var(--phys-ink)]'
                            }
                          `}>
                            {getVariantLabel(variant)}
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  {renderPriceBlock('justify-center')}

                  <button
                    onClick={handleAddToCart}
                    disabled={!inStock || buttonState === 'loading'}
                    className={getDesktopButtonClasses()}
                  >
                    {inStock ? getButtonContent() : 'Agotado'}
                  </button>
                </div>

                {/* Default: resumen sobrepuesto en la posición exacta de los swatches */}
                <div className="absolute top-0 left-0 right-0 group-hover:invisible">
                  <p className="text-sm text-[var(--phys-slate)] text-center font-light leading-[20px]">
                    {getVariantSummary()} / Desde ${minPrice}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ============================================
            PRODUCTO SIMPLE
            ============================================ */}
        {!isVariable && (
          <div className="mt-auto">
            <div className="md:mt-auto">
              <p
                className={`text-sm text-[var(--phys-slate)] text-left md:text-center font-light mb-3 ${productSize ? '' : 'invisible'}`}
                aria-hidden={productSize ? undefined : true}
              >
                {productSize || ' '}
              </p>

              {renderPriceBlock('justify-start md:justify-center')}
            </div>

            {/* MOBILE: Botón siempre visible, compacto */}
            <div className="md:hidden">
              <button
                onClick={handleAddToCart}
                disabled={!inStock || buttonState === 'loading'}
                className={getMobileButtonClasses()}
              >
                {inStock ? getButtonContent() : 'Agotado'}
              </button>
            </div>

            {/* DESKTOP: Botón solo en hover */}
            <div className="hidden md:block">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <button
                  onClick={handleAddToCart}
                  disabled={!inStock || buttonState === 'loading'}
                  className={getDesktopButtonClasses()}
                >
                  {inStock ? getButtonContent() : 'Agotado'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
